import React, { useState, useEffect } from 'react';
import './index.css';
import ruppezy_icon from "../../assets/icons/rupeezy_icon.svg";
import tv_terminal_icon from "../../assets/icons/tv_terminal.svg";
import vortex_icon from "../../assets/icons/vortex.svg";
import dock_icon from "../../assets/icons/dock.svg";
import rupeezy_icon_with_name from "../../assets/icons/rupeezy.svg";
import aero_icon from "../../assets/icons/aero.svg"; // Add your aero icon path
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import CryptoJS from 'crypto-js';
import flowBackEndServices2 from '../../services/flowBackendServices';
import { setActiveAccount } from '../../slices/authReducer';
import type { ActiveAccount } from '../../slices/authReducer';
import AccountPickerModal from './AccountPickerModal';
import type { LinkedAccount } from './AccountPickerModal';

interface AuthLoginData {
    refreshToken: string;
    ssoToken: string;
    deviceId: string;
    tradingActive: boolean;
}

const accountOf = (accounts: LinkedAccount[], clientCode: string): ActiveAccount => ({
    clientCode,
    accountType: accounts.find((account) => account.client_code === clientCode)?.account_type,
});

const RupeezyLogin: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [ssoToken, setSsoToken] = useState<string | null>(null);
    const [isCheckingAccounts, setIsCheckingAccounts] = useState(false);
    const [showAccountPicker, setShowAccountPicker] = useState(false);
    const [linkedAccounts, setLinkedAccounts] = useState<LinkedAccount[]>([]);
    const [pendingWebSsoToken, setPendingWebSsoToken] = useState<string | null>(null);
    const [pendingAccessToken, setPendingAccessToken] = useState<string | null>(null);

    useEffect(() => {
        const authLoginData = localStorage.getItem("AUTH_LOGIN");
        if (authLoginData) {
            try {
                const parsedData: AuthLoginData = JSON.parse(authLoginData);
                setSsoToken(parsedData.ssoToken);
            } catch (error) {
                console.error("Error parsing AUTH_LOGIN data:", error);
            }
        } else {
            console.error("No SSO token found in local storage");
        }
    }, []);

    const redirectToWeb = (token: string) => {
        const baseUrl = process.env.REACT_APP_APP_URL || 'https://flow.rupeezy.dev';
        const params = new URLSearchParams({ sso_auth_token: token, version: 'v2' });
        window.open(`${baseUrl}/web?${params.toString()}`, "_self");
    };

    // Analytics reads the account from the store, so it has to be set before
    // the page leaves for Rupeezy Web.
    const enterWebAs = (token: string, account: ActiveAccount) => {
        if (account.clientCode) dispatch(setActiveAccount(account));
        redirectToWeb(token);
    };

    const handleNavigateWithStoredToken = () => {
        if (ssoToken) {
            const baseUrl = process.env.REACT_APP_APP_URL || 'https://flow.rupeezy.dev';
            const path = '/tvTerminal/sso';
            const params = new URLSearchParams({
                sso_auth_token: ssoToken,
                version: 'v2'
            });
            const fullRedirectUrl = `${baseUrl}${path}?${params.toString()}`;
            window.open(fullRedirectUrl, "_self");
        } else {
            window.open('/', "_self");
        }
    };

    const handleNavigateWithStoredTokenForWeb = async () => {
        if (!ssoToken) {
            window.open('/', "_self");
            return;
        }

        const webAppId = process.env.REACT_APP_WEB_APPLICATION_ID || 'at_kPb7nG2qL8';
        setIsCheckingAccounts(true);

        try {
            const response = await flowBackEndServices2.verifySso(ssoToken, webAppId);
            const result = response.data;

            if (result?.status !== 'success') {
                console.error("verifySso failed:", result?.status, result?.message);
                alert("Failed to verify your session. Please log in again.");
                window.open('/', "_self");
                return;
            }

            const newSsoToken: string = result.ssoToken || ssoToken;
            const accessToken: string = result.data?.access_token || '';
            const accounts: LinkedAccount[] = result.linked_accounts ?? [];
            const primaryClientCode: string = result.primary_client_code ?? '';

            if (accounts.length <= 1) {
                // Single account — go home as usual. The backend only lists
                // linked accounts when there are several, so the code usually
                // comes from the session and the type is unknown.
                enterWebAs(newSsoToken, {
                    clientCode: accounts[0]?.client_code ?? result.data?.user_id,
                    accountType: accounts[0]?.account_type,
                });
            } else if (primaryClientCode) {
                // Multiple accounts + primary set → auto-switch silently
                try {
                    const switchResp = await flowBackEndServices2.switchAccount(primaryClientCode, accessToken);
                    const switchedToken: string = switchResp.data?.ssoToken || '';
                    if (!switchedToken) {
                        throw new Error("switchAccount did not return a ssoToken");
                    }
                    enterWebAs(switchedToken, accountOf(accounts, primaryClientCode));
                } catch (err) {
                    console.error("Auto-switch to primary account failed:", err);
                    // Fall back to the picker so the user can manually select
                    setLinkedAccounts(accounts);
                    setPendingWebSsoToken(newSsoToken);
                    setPendingAccessToken(accessToken);
                    setShowAccountPicker(true);
                }
            } else {
                // Multiple accounts + no primary → mandatory picker
                setLinkedAccounts(accounts);
                setPendingWebSsoToken(newSsoToken);
                setPendingAccessToken(accessToken);
                setShowAccountPicker(true);
            }
        } catch (err) {
            console.error("verifySso error:", err);
            alert("Failed to verify your session. Please log in again.");
            window.open('/', "_self");
        } finally {
            setIsCheckingAccounts(false);
        }
    };

    const handleAccountSelect = async (clientCode: string) => {
        setShowAccountPicker(false);

        if (!pendingAccessToken || !pendingWebSsoToken) {
            if (ssoToken) redirectToWeb(ssoToken);
            return;
        }

        try {
            const response = await flowBackEndServices2.switchAccount(clientCode, pendingAccessToken, true);
            const switchedToken: string = response.data?.ssoToken || '';
            if (!switchedToken) {
                throw new Error("switchAccount did not return a ssoToken");
            }
            enterWebAs(switchedToken, accountOf(linkedAccounts, clientCode));
        } catch (err) {
            console.error("Switch account failed:", err);
            alert("Account switch failed. Please try again.");
            setShowAccountPicker(true);
        }
    };

    const handleNavigateWithStoredTokenForDock = () => {
        if (ssoToken) {
            const baseUrl = process.env.REACT_APP_DOCK_BASE_URL || 'https://dock.rupeezy.dev';
            const path = '/dashboard';
            const params = new URLSearchParams({
                sso_auth_token: ssoToken,
                version: 'v2'
            });
            const fullRedirectUrl = `${baseUrl}${path}?${params.toString()}`;
            window.open(fullRedirectUrl, "_self");
        } else {
            window.open('/', "_self");
        }
    };

    const handleNavigateWithStoredTokenForAero = () => {
        if (ssoToken) {
            const baseUrl = process.env.REACT_APP_AERO_URL || 'https://aero.rupeezy.in/AeroSso';
            const path = '/login';
            const params = new URLSearchParams({
                token: ssoToken,
                applicationId: process.env.REACT_APP_APPLICATION_ID_FOR_AERO || '',
                ucc: "",
                data: ""
            });
            const fullRedirectUrl = `${baseUrl}${path}?${params.toString()}`;
            window.open(fullRedirectUrl, "_self");
        } else {
            window.open('/', "_self");
        }
    };

    return (
        <div className="rupeezy-container">
            {showAccountPicker && (
                <AccountPickerModal
                    accounts={linkedAccounts}
                    onSelect={handleAccountSelect}
                />
            )}

            <div className="login-container">
                <div className="login-card">
                    <div className="logo-section">
                        <img src={rupeezy_icon_with_name} alt="Rupeezy Logo" />
                    </div>

                    <div className="login-content">
                        <h3 className="login-title">Login</h3>
                        <p className="login-subtitle">Choose the product you want to access.</p>

                        <div className="product-options">
                            <button
                                autoFocus
                                type="button"
                                className="product-card rupeezy-web"
                                onClick={handleNavigateWithStoredTokenForWeb}
                                style={isCheckingAccounts ? { pointerEvents: 'none', opacity: 0.6 } : {}}
                            >
                                <div className="product-icon">
                                    <img src={ruppezy_icon} alt="Rupeezy Web Icon" />
                                </div>
                                <h4 className="product-title">RUPEEZY WEB</h4>
                                <p className="product-description">
                                    {isCheckingAccounts ? 'Loading...' : 'Trade & invest, track portfolios, and manage your investments.'}
                                </p>
                            </button>

                            <button
                                type="button"
                                className="product-card trading-view"
                                onClick={handleNavigateWithStoredToken}
                            >
                                <div className="product-icon">
                                    <img src={tv_terminal_icon} alt="TradingView Icon" />
                                </div>
                                <h4 className="product-title">TradingView</h4>
                                <p className="product-description">
                                    View live market data, analytics, real-time updates, and advanced charts.
                                </p>
                            </button>

                            <button
                                type="button"
                                className="product-card dock"
                                onClick={handleNavigateWithStoredTokenForDock}
                            >
                                <div className="product-icon">
                                    <img src={dock_icon} alt="Dock Icon" />
                                </div>
                                <h4 className="product-title" style={{ color: "black" }}>Dock</h4>
                                <p className="product-description" style={{ color: "black" }}>
                                    Dashboard to track P&L, view trade history, manage account details, and more.
                                </p>
                            </button>

                            {/* <div 
                                className="product-card aero"
                                onClick={handleNavigateWithStoredTokenForAero}
                            >
                                <div className="discontinuing-banner">Discontinuing Soon</div>
                                <div className="product-icon">
                                    <img src={aero_icon} alt="Aero Icon" />
                                </div>
                                <h4 className="product-title">Aero</h4>
                                <p className="product-description">
                                    Aero is discontinuing soon. Try <b>Rupeezy Web</b> for faster and better experience.
                                </p>
                            </div> */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RupeezyLogin;

